import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowRight, ChevronDown, LogIn, Home } from "lucide-react";
import Logo from "@/components/Logo";
import LanguageSwitcher from "@/i18n/LanguageSwitcher";
import CurrencySwitcher from "@/i18n/CurrencySwitcher";
import { useLang } from "@/i18n/LanguageContext";

export function scrollToId(id) {
  const el = document.getElementById(id);
  if (!el) return;
  const top = el.getBoundingClientRect().top + window.scrollY - 76;
  window.scrollTo({ top, behavior: "smooth" });
}

export function useSectionNav() {
  const navigate = useNavigate();
  const location = useLocation();
  return (id) => {
    if (location.pathname === "/") {
      scrollToId(id);
    } else {
      navigate("/", { state: { scrollTo: id } });
    }
  };
}

function Dropdown({ label, items, testid, onNavigate }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button
        data-testid={testid}
        onClick={() => setOpen(!open)}
        className="text-sm font-semibold tracking-wide transition-colors duration-200 flex items-center gap-1.5 text-[#FAF6EE]/90 hover:text-[#D9A854]"
      >
        {label}
        <ChevronDown size={13} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.18 }}
            className="absolute top-full left-1/2 -translate-x-1/2 pt-3 z-50"
          >
            <div className="bg-white rounded-2xl border border-[#E6DED1] shadow-[0_16px_40px_-8px_rgba(11,46,74,0.25)] p-2 min-w-[220px]">
              {items.map((item, i) => (
                <button
                  key={i}
                  data-testid={`${testid}-item-${i}`}
                  onClick={() => {
                    setOpen(false);
                    onNavigate(item);
                  }}
                  className="w-full text-left px-4 py-2.5 rounded-xl text-sm font-semibold text-[#0B2E4A] hover:bg-[#F1E7D6] hover:text-[#7A571C] transition-colors"
                >
                  {item.label}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Navbar() {
  const { t } = useLang();
  const navigate = useNavigate();
  const goSection = useSectionNav();
  const [open, setOpen] = useState(false);

  const SERVICES = [
    { type: "route", to: "/viaje-a-medida", label: t("nav.medida") },
    { type: "route", to: "/viajes-en-grupo", label: t("nav.dates") },
    { type: "route", to: "/concierge-casamentos", label: t("nav.casamentos") },
    { type: "route", to: "/lua-de-mel", label: t("nav.honeymoon") },
  ];
  const RESOURCES = [
    { type: "route", to: "/brasil", label: t("nav.brasil") },
    { type: "route", to: "/recursos/blog", label: "Blog" },
    { type: "section", id: "faq", label: "FAQs" },
    { type: "route", to: "/quiz", label: t("nav.cta") },
    { type: "route", to: "/recursos/descargables", label: t("nav.downloads") },
  ];

  const onNavigate = (item) => {
    setOpen(false);
    if (item.type === "route") {
      navigate(item.to);
      window.scrollTo(0, 0);
    } else {
      goSection(item.id);
    }
  };

  const goHome = () => {
    setOpen(false);
    goSection("inicio");
  };

  return (
    <header
      data-testid="main-navbar"
      className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-[#0B2E4A]/95 border-b border-white/10 shadow-[0_8px_30px_-12px_rgba(11,46,74,0.5)]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-4 flex items-center justify-between gap-3">
        <Logo dark />
        <nav className="hidden lg:flex items-center gap-7" data-testid="nav-links">
          <button
            data-testid="nav-home"
            onClick={goHome}
            className="text-sm font-semibold tracking-wide transition-colors duration-200 flex items-center gap-1.5 text-[#FAF6EE]/90 hover:text-[#D9A854]"
          >
            <Home size={14} /> {t("nav.home")}
          </button>
          <Dropdown label={t("nav.servicios")} items={SERVICES} testid="nav-servicios" onNavigate={onNavigate} />
          <Dropdown label={t("nav.recursos")} items={RESOURCES} testid="nav-recursos" onNavigate={onNavigate} />
        </nav>
        <div className="hidden lg:flex items-center gap-4">
          <button
            data-testid="nav-contact-btn"
            onClick={() => goSection("contacto")}
            className="bg-[#D9A854] hover:bg-[#C99944] text-[#0B2E4A] text-sm font-semibold px-6 py-2.5 rounded-full transition-all duration-200 flex items-center gap-2 shadow-lg shadow-[#D9A854]/25 whitespace-nowrap hover:scale-[1.03] active:scale-95"
          >
            {t("nav.contact")} <ArrowRight size={15} />
          </button>
          <button
            data-testid="nav-login"
            onClick={() => navigate("/login")}
            className="text-xs font-bold uppercase tracking-[0.15em] transition-colors flex items-center gap-1.5 text-[#F1E7D6]/70 hover:text-[#D9A854]"
          >
            <LogIn size={13} /> {t("nav.login")}
          </button>
          <CurrencySwitcher dark />
          <LanguageSwitcher dark />
        </div>
        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen(!open)}
          className="lg:hidden p-2 text-[#FAF6EE]"
          aria-label={t("nav.menu")}
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>
      {open && (
        <div className="lg:hidden px-6 pb-6 pt-2 flex flex-col gap-4 bg-[#0B2E4A] border-t border-white/10" data-testid="nav-mobile-menu">
          <button data-testid="nav-mobile-home" onClick={goHome} className="text-left text-base font-semibold text-[#FAF6EE] py-0.5 flex items-center gap-2">
            <Home size={15} className="text-[#D9A854]" /> {t("nav.home")}
          </button>
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#D9A854]">{t("nav.servicios")}</p>
          {SERVICES.map((l, i) => (
            <button key={i} data-testid={`nav-mobile-servicios-${i}`} onClick={() => onNavigate(l)} className="text-left text-base font-semibold text-[#FAF6EE] py-0.5 pl-3">
              {l.label}
            </button>
          ))}
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#D9A854] mt-2">{t("nav.recursos")}</p>
          {RESOURCES.map((l, i) => (
            <button key={i} data-testid={`nav-mobile-recursos-${i}`} onClick={() => onNavigate(l)} className="text-left text-base font-semibold text-[#FAF6EE] py-0.5 pl-3">
              {l.label}
            </button>
          ))}
          <button
            data-testid="nav-mobile-contact-btn"
            onClick={() => { setOpen(false); goSection("contacto"); }}
            className="bg-[#D9A854] text-[#0B2E4A] font-semibold px-6 py-3 rounded-full flex items-center justify-center gap-2"
          >
            {t("nav.contact")} <ArrowRight size={15} />
          </button>
          <div className="py-1 flex items-center gap-3">
            <CurrencySwitcher dark />
            <LanguageSwitcher dark />
          </div>
          <button
            data-testid="nav-mobile-login-btn"
            onClick={() => { setOpen(false); navigate("/login"); }}
            className="text-left text-sm font-bold text-[#F1E7D6]/70 py-1 flex items-center gap-2"
          >
            <LogIn size={13} /> {t("nav.login")}
          </button>
        </div>
      )}
    </header>
  );
}
